import { Card, CardContent, CardFooter, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useMousedownScroll } from '@/hooks/shared/useMousedownScroll';
import React, { forwardRef, ReactNode } from 'react';

export interface BaseCardProps {
  children: ReactNode;
  title?: ReactNode;
  footer?: ReactNode;
  variant?: 'wrapper' | 'base';
  classNames?: { card?: string; title?: string; footer?: string; content?: string };
  dataTestId?: string;
  onClick?: () => any;
}

const cardStyleByVariant = {
  base: {
    // card: 'max-w-screen p-2 max-h-screen rounded-none border-none relative min-h-fit',
    card: 'max-w-screen p-2 max-h-screen rounded-none border-none relative min-h-full dark:bg-dark-bg-elevated',
    title: 'pt-0 pt-1 pb-2 pl-2 font-semibold text-sm capitalized flex items-center',
    content: 'p-2',
    footer: '',
  },
  wrapper: {
    card: 'bg-gray-200 dark:bg-dark-bg-elevated rounded-lg border-2 dark:border-dark-border min-h-fit',
    content: 'bg-white dark:bg-dark-bg-sidebar',
    title: '',
    footer: '',
  },
};

export const BaseCard = forwardRef(
  (
    { children, title, variant = 'base', footer, classNames, onClick, dataTestId, ...props }: BaseCardProps,
    ref: React.Ref<HTMLDivElement> // Specify the type of the ref (HTMLDivElement or the element you are referencing)
  ) => {
    return (
      <Card
        data-testid={dataTestId} //
        ref={ref}
        {...props}
        className={cn([`${cardStyleByVariant.base.card}`, `${cardStyleByVariant[variant].card}`, classNames?.card])}
        onClick={onClick}
      >
        {title && (
          <CardTitle
            className={cn([
              `${cardStyleByVariant.base.title}`,
              `${cardStyleByVariant[variant].title}`,
              classNames?.title,
            ])}
          >
            {title}
          </CardTitle>
        )}
        <CardContent
          className={cn([
            `${cardStyleByVariant.base.content}`,
            `${cardStyleByVariant[variant].content}`,
            classNames?.content,
          ])}
        >
          {children}
        </CardContent>
        {footer && (
          <CardFooter
            className={cn([
              `${cardStyleByVariant.base.footer}`,
              `${cardStyleByVariant[variant].footer}`,
              classNames?.footer,
            ])}
          >
            {footer}
          </CardFooter>
        )}
      </Card>
    );
  }
);

BaseCard.displayName = 'BaseCard';
